import { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Shield, UserCog, Phone, Eye, Check, ChevronsUpDown
} from 'lucide-react'; 
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem,
} from '@/components/ui/command';

import { BANCOS_BRASIL } from '@/lib/bancos.js'; 

const TIPOS_CHAVE_PIX = ["CPF", "CNPJ", "Email", "Telefone", "Chave Aleatória"];

const initialVendedorForm = {
  nome: '', email: '', telefone: '', cpf: '', data_nascimento: '', pix: '', pix_tipo: '', pix_banco: ''
};

export default function Colaboradores() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);

    // Verifica se é admin para mostrar menus sensíveis
  const isAdmin = user.role === 'admin' || user.is_superuser;

  // Estados
  const [openVendedor, setOpenVendedor] = useState(false);
  const [editingVendedorId, setEditingVendedorId] = useState(null);
  const [vendedorForm, setVendedorForm] = useState(initialVendedorForm);
  const [openVendedorDetails, setOpenVendedorDetails] = useState(false);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [openVendedorBancoPopover, setOpenVendedorBancoPopover] = useState(false);

  useEffect(() => {
    loadVendedores();
  }, []);

  const loadVendedores = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/vendedores');
      setVendedores(res.data);
    } catch {
      toast.error('Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  };

  // --- MÁSCARAS ---
  const handleTelefoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
    value = value.replace(/(\d)(\d{4})$/, '$1-$2');
    setVendedorForm(prev => ({ ...prev, telefone: value }));
  };

  const handleCpfChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    value = value.replace(/(\d{3})(\d)/, '$1.$2');
    value = value.replace(/(\d{3})(\d)/, '$1.$2');
    value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    setVendedorForm(prev => ({ ...prev, cpf: value }));
  };

  const formatDataDisplay = (isoDate) => {
    if (!isoDate) return "N/A";
    try {
        const date = new Date(isoDate);
        return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' }); 
    } catch {
        return isoDate;
    }
  };

  const handleVendedorSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...vendedorForm };
      payload.pix = payload.pix || '';
      payload.pix_tipo = payload.pix_tipo || '';
      payload.pix_banco = payload.pix_banco || '';

      delete payload.username;
      delete payload.password;

      if (editingVendedorId) {
        await axiosInstance.put(`/vendedores/${editingVendedorId}`, payload);
        toast.success('Colaborador atualizado!');
      } else {
        await axiosInstance.post('/vendedores', payload); 
        toast.success('Colaborador cadastrado!');
      }

      setOpenVendedor(false);
      setEditingVendedorId(null);
      setVendedorForm(initialVendedorForm);
      loadVendedores();

    } catch (error) {
      console.error(error);
      let errorMessage = 'Erro ao salvar colaborador';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : 'Erro de validação';
      }
      toast.error(errorMessage);
    }
  };

  const handleEditVendedor = (v) => {
    setEditingVendedorId(v.id);
    setVendedorForm({
      nome: v.nome || '', email: v.email || '', telefone: v.telefone || '',
      cpf: v.cpf || '', data_nascimento: v.data_nascimento || '',
      pix: v.pix || '', pix_tipo: v.pix_tipo || '', pix_banco: v.pix_banco || ''
    });
    setOpenVendedor(true);
  };

  const handleViewVendedor = (v) => {
    setSelectedVendedor(v);
    setOpenVendedorDetails(true);
  };

  const handleDeleteVendedor = async (id) => {
    try {
      await axiosInstance.delete(`/vendedores/${id}`);
      toast.success('Colaborador removido!');
      loadVendedores();
    } catch {
      toast.error('Erro ao deletar colaborador');
    }
  };

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex justify-between items-center mb-6 pt-8">
        <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Colaboradores</h1>
            <p className="text-gray-600">Equipe de vendas e administração</p>
        </div>
        {/* BOTÃO NOVO COLABORADOR - Condicional removida se quiser que todos vejam, ou mantenha se for só SuperUser */}
        {(user.role === 'admin' || user.is_superuser) && (
            <Button onClick={() => { setEditingVendedorId(null); setVendedorForm(initialVendedorForm); setOpenVendedor(true); }} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Plus className="w-4 h-4 mr-2" /> Novo Colaborador
            </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
            <thead className="bg-gray-50 border-b">
            <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Colaborador</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chave Pix</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Função</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
            </tr>
            </thead>
            <tbody className="divide-y">
            {vendedores.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                        <div className={`p-2 rounded-full mr-3 ${v.user_id === user.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>
                            {v.user_id === user.id ? <Shield className="w-4 h-4" /> : <UserCog className="w-4 h-4" />}
                        </div>
                        <div>
                            <div className="text-sm font-medium text-gray-900">{v.nome}</div>
                            <div className="text-xs text-gray-500">{v.cpf || 'CPF n/d'}</div>
                        </div>
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{v.email}</div>
                        <div className="text-xs text-gray-500 flex items-center mt-0.5"><Phone className="w-3 h-3 mr-1" /> {v.telefone || 'N/A'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap"><span className="text-sm text-gray-700 font-mono">{v.pix || '-'}</span></td>
                <td className="px-6 py-4 whitespace-nowrap">
                    {v.user_id === user.id ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 border border-blue-100">Admin</span>
                    ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-md bg-gray-50 text-gray-700 border border-gray-100">Vendedor</span>
                    )}
                </td>
                <td className="px-6 py-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleViewVendedor(v)} className="mr-1"><Eye className="w-4 h-4 text-blue-600" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEditVendedor(v)}><Pencil className="w-4 h-4" /></Button>
                    <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="sm"><Trash2 className="w-4 h-4 text-red-600" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle><AlertDialogDescription>Deseja deletar este colaborador?</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteVendedor(v.id)} style={{ background: '#ef4444' }}>Deletar</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                    </AlertDialog>
                </td>
                </tr>
            ))}
            </tbody>
        </table>
      </div>

      {/* MODAL COLABORADOR */}
      <Dialog open={openVendedor} onOpenChange={setOpenVendedor}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingVendedorId ? 'Editar' : 'Novo'} Colaborador</DialogTitle></DialogHeader>
            <form onSubmit={handleVendedorSubmit} className="space-y-4 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label htmlFor="nome">Nome Completo</Label><Input id="nome" value={vendedorForm.nome} onChange={(e) => setVendedorForm({ ...vendedorForm, nome: e.target.value })} required placeholder="Ex: João da Silva" /></div>
                <div><Label htmlFor="cpf">CPF</Label><Input id="cpf" value={vendedorForm.cpf} onChange={handleCpfChange} placeholder="000.000.000-00" required /></div>
                <div><Label htmlFor="data_nascimento">Data de Nascimento</Label><Input id="data_nascimento" type="date" value={vendedorForm.data_nascimento} onChange={(e) => setVendedorForm({ ...vendedorForm, data_nascimento: e.target.value })} /></div>
                <div><Label htmlFor="telefone">Telefone / WhatsApp</Label><Input id="telefone" value={vendedorForm.telefone} onChange={handleTelefoneChange} placeholder="(00) 00000-0000" /></div>
                <div className="md:col-span-2"><Label htmlFor="email">Email Corporativo</Label><Input id="email" type="email" value={vendedorForm.email} onChange={(e) => setVendedorForm({ ...vendedorForm, email: e.target.value })} /></div>

                {/* ÁREA PIX */}
                <div className="md:col-span-2 border rounded-lg p-4 bg-gray-50 space-y-3">
                    <Label className="text-blue-600 font-semibold">Dados Bancários (Pix)</Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs text-gray-500">Tipo de Chave</Label>
                            <Select value={vendedorForm.pix_tipo} onValueChange={(val) => setVendedorForm({ ...vendedorForm, pix_tipo: val })}>
                                <SelectTrigger className="bg-white"><SelectValue placeholder="Tipo" /></SelectTrigger>
                                <SelectContent>{TIPOS_CHAVE_PIX.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label className="text-xs text-gray-500">Chave Pix</Label><Input value={vendedorForm.pix} onChange={(e) => setVendedorForm({ ...vendedorForm, pix: e.target.value })} className="bg-white" /></div>
                        <div>
                            <Label className="text-xs text-gray-500">Banco</Label>
                            <Popover open={openVendedorBancoPopover} onOpenChange={setOpenVendedorBancoPopover}>
                              <PopoverTrigger asChild>
                                <Button variant="outline" role="combobox" className="w-full justify-between bg-white">
                                  {vendedorForm.pix_banco ? vendedorForm.pix_banco : "Selecione..."}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                <Command>
                                  <CommandInput placeholder="Pesquisar..." />
                                  <CommandEmpty>Não encontrado.</CommandEmpty>
                                  <CommandGroup className="max-h-60 overflow-y-auto">
                                    {BANCOS_BRASIL.map((banco) => (
                                      <CommandItem key={banco} value={banco} onSelect={(val) => { setVendedorForm({ ...vendedorForm, pix_banco: val }); setOpenVendedorBancoPopover(false); }}>
                                        <Check className={`mr-2 h-4 w-4 ${vendedorForm.pix_banco === banco ? "opacity-100" : "opacity-0"}`} />
                                        {banco}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </Command>
                              </PopoverContent>
                            </Popover>
                        </div>
                    </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenVendedor(false)}>Cancelar</Button>
                <Button type="submit" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>{editingVendedorId ? 'Atualizar' : 'Cadastrar'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
      </Dialog>

      {/* MODAL DETALHES COLABORADOR */}
      <Dialog open={openVendedorDetails} onOpenChange={setOpenVendedorDetails}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Detalhes do Colaborador</DialogTitle><DialogDescription>Informações cadastrais e financeiras.</DialogDescription></DialogHeader>
            {selectedVendedor && (
              <div className="space-y-4 py-2">
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`p-3 rounded-full ${selectedVendedor.user_id === user.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>{selectedVendedor.user_id === user.id ? <Shield className="w-6 h-6" /> : <UserCog className="w-6 h-6" />}</div>
                  <div><h3 className="font-semibold text-lg">{selectedVendedor.nome}</h3><p className="text-sm text-gray-500">{selectedVendedor.user_id === user.id ? "Administrador" : "Vendedor"}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><Label className="text-xs text-gray-500">CPF</Label><p className="font-medium mt-0.5">{selectedVendedor.cpf || 'N/A'}</p></div>
                  <div><Label className="text-xs text-gray-500">Nascimento</Label><p className="font-medium mt-0.5">{formatDataDisplay(selectedVendedor.data_nascimento)}</p></div>
                  <div><Label className="text-xs text-gray-500">Telefone</Label><p className="font-medium mt-0.5">{selectedVendedor.telefone || 'N/A'}</p></div>
                  <div><Label className="text-xs text-gray-500">Email</Label><p className="font-medium truncate">{selectedVendedor.email || 'N/A'}</p></div>
                  <div className="col-span-2 pt-2 border-t border-gray-100">
                    <Label className="text-xs text-gray-500">Dados Bancários (Pix)</Label>
                    <div className="bg-gray-50 p-3 rounded border border-gray-100 mt-1">
                        <div className="grid grid-cols-2 gap-2">
                            <div><span className="text-xs text-gray-400 block">Banco</span><span className="text-sm font-medium text-gray-700">{selectedVendedor.pix_banco || '-'}</span></div>
                            <div><span className="text-xs text-gray-400 block">Tipo</span><span className="text-sm font-medium text-gray-700">{selectedVendedor.pix_tipo || '-'}</span></div>
                            <div className="col-span-2 mt-1"><span className="text-xs text-gray-400 block">Chave</span><span className="font-mono text-sm font-semibold text-gray-900 select-all">{selectedVendedor.pix || 'Não cadastrada'}</span></div>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter><Button type="button" onClick={() => setOpenVendedorDetails(false)}>Fechar</Button></DialogFooter>
          </DialogContent>
      </Dialog>
    </div>
  );
}